import React, { useMemo, useState } from 'react';
import { CheckCircle2, AlertTriangle, Plus } from 'lucide-react';
import Modal from './Modal';
import MUST_NOT_MISS, { matchesDiagnosis } from '../data/mustNotMiss';
import { toast } from '../utils/toast';

const CUSTOM_KEY = 'cdmgrid:customChecklist';

/**
 * Checks the group's diagnoses against a must-not-miss checklist for a chosen
 * chief complaint; missing entries can be added as Critical Dx rows in one click.
 */
export default function ChecklistModal({ cdmRows, isFacilitator, onAddCritical, onClose }) {
  const complaints = Object.keys(MUST_NOT_MISS);
  const [complaint, setComplaint] = useState(complaints[0]);
  const [customText, setCustomText] = useState(() => localStorage.getItem(CUSTOM_KEY) || '');
  const [selected, setSelected] = useState(() => new Set());

  const isCustom = complaint === '__custom__';
  const checklist = useMemo(() => {
    if (!isCustom) return MUST_NOT_MISS[complaint] || [];
    return customText
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean)
      .map((name) => ({ name, aliases: [name.toLowerCase()] }));
  }, [complaint, isCustom, customText]);

  const allDxTexts = useMemo(
    () => cdmRows.flatMap((r) => (r.diagnoses || []).map((d) => d.text)),
    [cdmRows]
  );

  const { covered, missing } = useMemo(() => {
    const covered = [];
    const missing = [];
    checklist.forEach((item) => {
      const hit = allDxTexts.find((t) => matchesDiagnosis(t, item));
      if (hit) covered.push({ ...item, matchedText: hit });
      else missing.push(item);
    });
    return { covered, missing };
  }, [checklist, allDxTexts]);

  const toggleSelected = (name) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  };

  const addSelected = () => {
    const names = missing.map((m) => m.name).filter((n) => selected.has(n));
    if (!names.length) return;
    onAddCritical(names);
    toast(`Added ${names.length} critical diagnos${names.length === 1 ? 'is' : 'es'} to the grid.`, 'success');
    onClose();
  };

  return (
    <Modal title="Must-not-miss checklist" onClose={onClose} wide>
      <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-4">
        <label className="text-meta font-medium text-ink-2" htmlFor="complaint">Chief complaint:</label>
        <select
          id="complaint"
          value={complaint}
          onChange={(e) => { setComplaint(e.target.value); setSelected(new Set()); }}
          className="px-3 py-2 border border-line rounded-ctl text-body outline-none focus:ring-2 focus:border-accent focus:ring-accent/25 bg-surface"
        >
          {complaints.map((c) => <option key={c} value={c}>{c}</option>)}
          <option value="__custom__">Custom list…</option>
        </select>
      </div>

      {isCustom && (
        <textarea
          value={customText}
          onChange={(e) => { setCustomText(e.target.value); localStorage.setItem(CUSTOM_KEY, e.target.value); }}
          placeholder={'One must-not-miss diagnosis per line…'}
          rows={5}
          className="w-full mb-4 px-3 py-2 border border-line rounded-ctl text-body outline-none focus:ring-2 focus:border-accent focus:ring-accent/25 resize-y"
        />
      )}

      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <h3 className="flex items-center gap-1.5 text-meta font-semibold text-flag-green uppercase tracking-wide mb-2">
            <CheckCircle2 className="w-3.5 h-3.5" />Considered ({covered.length})
          </h3>
          <ul className="space-y-1.5">
            {covered.map((c) => (
              <li key={c.name} className="text-body text-ink bg-flag-green/5 border border-flag-green/40 rounded-ctl px-3 py-2">
                {c.name}
                <span className="block text-meta text-flag-green/70">matched: “{c.matchedText}”</span>
              </li>
            ))}
            {!covered.length && <li className="text-meta text-ink-2 italic">None yet</li>}
          </ul>
        </div>
        <div>
          <h3 className="flex items-center gap-1.5 text-meta font-semibold text-flag-red uppercase tracking-wide mb-2">
            <AlertTriangle className="w-3.5 h-3.5" />Not considered ({missing.length})
          </h3>
          <ul className="space-y-1.5">
            {missing.map((m) => (
              <li key={m.name}>
                <label className="flex items-center gap-2 text-body text-ink bg-flag-red/5 border border-flag-red/40 rounded-ctl px-3 py-2 cursor-pointer">
                  {isFacilitator && (
                    <input
                      type="checkbox"
                      checked={selected.has(m.name)}
                      onChange={() => toggleSelected(m.name)}
                      className="accent-flag-red"
                    />
                  )}
                  {m.name}
                </label>
              </li>
            ))}
            {!missing.length && <li className="text-meta text-ink-2 italic">All covered 🎉</li>}
          </ul>
        </div>
      </div>

      {isFacilitator && missing.length > 0 && (
        <div className="mt-4 flex justify-end">
          <button
            onClick={addSelected}
            disabled={!selected.size}
            className="flex items-center gap-1.5 bg-flag-red text-white px-4 py-2 rounded-ctl text-body font-medium hover:bg-flag-red/90 transition disabled:opacity-50"
          >
            <Plus className="w-4 h-4" />Add {selected.size || ''} selected as Critical Dx
          </button>
        </div>
      )}
      <p className="text-meta text-ink-2 mt-4">Teaching prompts only, not clinical guidance. Matching is by keyword and may miss synonyms.</p>
    </Modal>
  );
}
